import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as teamService from '../lib/api/teamService';
import type { CreateTeamRequest, UpdateTeamRequest, Team } from '../lib/api/teamService';
import { getCurrentUser } from '../lib/auth/session';

export const useTeams = () => {
  const queryClient = useQueryClient();
  
  // Fetch all teams for the current user
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: teamService.getUserTeams,
  });
  
  // Create a new team
  const createTeam = useMutation({
    mutationFn: async (data: CreateTeamRequest) => {
      // Get current user ID
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('You must be logged in to create a team');
      }
      
      // Set the createdBy field
      const requestWithCreator = {
        ...data,
        createdBy: user.id
      };
      
      const newTeam = await teamService.createTeam(requestWithCreator);
      
      // This should already happen in the createTeam function, but as a fallback
      // try to add the creator as a team member if needed
      try {
        await teamService.createTeamMember({
          teamId: newTeam.id,
          userId: user.id,
          role: 'admin',
          shiftType: 'both'
        });
      } catch (error) {
        console.warn('Failed to add creator as team member (may already exist)', error);
      }
      
      return newTeam;
    },
    onSuccess: (newTeam: Team) => {
      // Update teams cache
      queryClient.setQueryData(['teams'], (oldData: Team[] | undefined) => 
        oldData ? [...oldData, newTeam] : [newTeam]
      );
      
      // Invalidate teams cache to ensure we have fresh data
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
  
  // Create a team member
  const createTeamMember = useMutation({
    mutationFn: ({ teamId, data }: { teamId: string; data: Partial<teamService.TeamMember> }) => {
      if (!data.userId) {
        throw new Error('User ID is required');
      }
      
      return teamService.createTeamMember({
        teamId,
        userId: data.userId,
        role: data.role || 'provider',
        shiftType: data.shiftType || 'both'
      });
    },
    onSuccess: (newMember, variables) => {
      if (newMember) {
        // Invalidate team members cache to trigger a refetch
        queryClient.invalidateQueries({
          queryKey: ['teams', variables.teamId, 'members']
        });
        
        // Also invalidate teams list
        queryClient.invalidateQueries({
          queryKey: ['teams']
        });
      }
    },
  });
  
  // Update a team
  const updateTeam = useMutation({
    mutationFn: ({ teamId, data }: { teamId: string; data: UpdateTeamRequest }) => 
      teamService.updateTeam(teamId, data),
    onSuccess: (updatedTeam: Team) => {
      // Update teams cache
      queryClient.setQueryData(['teams'], (oldData: Team[] | undefined) => 
        oldData ? oldData.map(team => team.id === updatedTeam.id ? updatedTeam : team) : [updatedTeam]
      );
      
      // Update the specific team cache
      queryClient.setQueryData(['teams', updatedTeam.id], updatedTeam);
    },
  });
  
  // Update a team member
  const updateTeamMember = useMutation<
    teamService.TeamMember | null, 
    Error, 
    { teamId: string; userId: string; data: Partial<teamService.TeamMember> }
  >({
    mutationFn: ({ teamId, userId, data }) => 
      teamService.updateTeamMember(teamId, userId, data),
    onSuccess: (updatedMember, variables) => {
      if (updatedMember) {
        // Invalidate team members cache to trigger a refetch
        queryClient.invalidateQueries({
          queryKey: ['teams', variables.teamId, 'members']
        });
      }
    },
  });
  
  return {
    teams,
    createTeam,
    createTeamMember,
    updateTeam,
    updateTeamMember,
  };
};

// Separate hook for team members since it requires a teamId parameter
export const useTeamMembers = (teamId: string) => {
  return useQuery({
    queryKey: ['teams', teamId, 'members'],
    queryFn: () => teamService.getTeamMembers(teamId),
    enabled: !!teamId,
  });
}; 